import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, input, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RepairLineItemFormWithParts } from '../../../../form-model/cwo-repair-line-item.form.model';
import { Shop } from '../../../../models/shop.model';
import { CodeDescription } from '../../../../models/code-description.model';
import { Repair } from '../../../../models/repair.model';
import { RepairLocation } from '../../../../models/repair-location.model';
import { getTPIChar, ThirdPartyIndicator } from '../../../../models/tpi.model';
import { IMcTypeaheadData } from '@maersk-global/mds-components-core/mc-typeahead/types';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-label';
import '@maersk-global/mds-components-core/mc-select';
import '@maersk-global/mds-components-core/mc-typeahead';
import { combineLatest, ReplaySubject, takeUntil } from 'rxjs';
import { utils } from '../../../../helper/utils';
import { ApplicationtTextService } from '../../../../services/application-text.service';
import { ControlValidationService } from '../../../../services/control-validation.service';
import { typeaheadCodeExistValidator } from '../../../../shared-validators/typeahead-code-exist.validator';
import { environment } from '../../../../../environments/environment';
import { MasterDataService } from '../../../../services/master-data.service';
import { ToastrService } from '../../../../services/toastr-service';
import { Mode } from '../../../../models/mode.model';
import { BulkWorkOrderSharedDataService } from '../../../../services/bulk-work-order-shared-data.service';
import { Part } from '../../../../models/part.model';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { CreateWorkOrderRepairPartsComponent } from '../../../create-work-order/repair-parts/cwo-repair-parts.component';

@Component({
  selector: 'app-bulk-work-order-repair-line-item',
  imports: [ReactiveFormsModule, NgxSkeletonLoaderModule, CreateWorkOrderRepairPartsComponent],
  templateUrl: './bulk-work-order-repair-line-item.component.html',
  styleUrl: './bulk-work-order-repair-line-item.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BulkWorkOrderRepairLineItemComponent implements OnInit, OnDestroy {
  @Input({ required: true }) repairLineItemForm!: FormGroup<RepairLineItemFormWithParts>;
  index = input.required<number>();
  shop = input.required<Shop | null>();
  mode = input.required<Mode | null>();
  controlId = input.required<string>();
  damages = input.required<CodeDescription[]>();
  repairs = input.required<Repair[]>();
  repairLocations = input.required<RepairLocation[] | null>();
  tpiCodes = input.required<ThirdPartyIndicator[] | null>();
  @Output() repairLineItemRemoved = new EventEmitter<number>();
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  // Controls data
  damageCodesTypeahead: IMcTypeaheadData[] = [];
  repairCodesTypeahead: IMcTypeaheadData[] = [];
  repairLocationCodesTypeahead: IMcTypeaheadData[] = [];
  tpiCodesTypeahead: IMcTypeaheadData[] = [];

  // Parts state
  parts: Part[] = [];
  loadingParts = signal<boolean>(false);
  isPartsMandatory: boolean = false;
  showOptionalParts: boolean = false;
  repairLineItemAmountWithoutParts: number = 0;
  partsTotalAmount: number = 0;

  constructor(
    public appTextSvc: ApplicationtTextService,
    public ctrlValidSvc: ControlValidationService,
    private masterDataService: MasterDataService,
    private toastrService: ToastrService,
    private sharedDataService: BulkWorkOrderSharedDataService,
  ) {}
  ngOnInit(): void {
    this.damageCodesTypeahead = this.mapCodeDescriptionToTypeaheadData(this.damages());
    this.repairCodesTypeahead = this.mapCodeDescriptionToTypeaheadData(this.repairs());
    this.repairLocationCodesTypeahead = this.mapCodeDescriptionToTypeaheadData(this.repairLocations() ?? []);
    this.tpiCodesTypeahead = this.mapTPICodeToTypeaheadData(this.tpiCodes() ?? []);
    this.addValidatorsToRepairCode();
    this.addValidatorsToRepairLocationCode();
    this.addValidatorsToDamageCode();
    this.addValidatorsToTpiCode();
    this.calculateRepairItemTotalCost();
  }

  onRemoveRepairLineItemClicked() {
    this.repairLineItemRemoved.emit(this.index());
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  addOptionalParts() {
    this.showOptionalParts = true;
  }

  allPartsRemoved() {
    this.showOptionalParts = false;
    this.repairLineItemForm.controls.parts.clear();
  }

  onRepairPartsTotalChanged(event: number) {
    this.partsTotalAmount = event;
    this.repairLineItemForm.controls.itemTotalCost.setValue(
      utils.numberFormatter.format(this.repairLineItemAmountWithoutParts + this.partsTotalAmount),
    );
  }

  /**
   * @description Event handler when the repair code is selected from the typeahead control
   * Resets parts, fetches parts if allowParts is true, sets field values and validators.
   */
  onRepairCodeOptionSelected(event: FocusEvent) {
    this.parts = [];
    this.repairLineItemForm.controls.parts.clear();
    this.allPartsRemoved();
    const repairCode = (event.target as HTMLInputElement).value;
    const selectedRepair = this.repairs().find((repair) => repair.code === repairCode);
    if (selectedRepair) {
      this.addRequiredAndMaxValidators(selectedRepair);
      this.repairLineItemForm.controls.pieces.setValue(1);
      this.repairLineItemForm.controls.manHoursPerPiece.setValue(utils.formatNumberToTwoDigitDecimal(selectedRepair.maxLabourHours));
      this.repairLineItemForm.controls.materialCostPerPiece.setValue(utils.formatNumberToTwoDigitDecimal(selectedRepair.maxMaterialCost));
      if (selectedRepair.allowParts === true) {
        this.isPartsMandatory = selectedRepair.isPartsMandatory;
        this.getParts(selectedRepair);
      }
      this.repairLineItemForm.markAllAsTouched();
      if (environment.Features.EnablePQCValidation) {
        const shopCode = this.shop()?.maintenanceShopCode;
        if (this.isRepairCodePQCUpgrade(repairCode) && shopCode && this.mode()?.code === '03') {
          this.masterDataService
            .getShopInventoryAndForecastForEquipmentTypeAndGrade(shopCode, this.sharedDataService.equipmentSize)
            .subscribe((response) => {
              if (response.bookingForecastCount && response.totalEquipmentCount) {
                if (response.bookingForecastCount < response.totalEquipmentCount) {
                  this.toastrService.showToastr(this.appTextSvc.pqcWarningMessage, 'warning', { duration: 10000 });
                }
              }
            });
        }
      }
    }
  }

  private getParts(repairCode: Repair) {
    this.loadingParts.set(true);
    const shopCode = this.shop()?.maintenanceShopCode;
    const modeCode = this.mode()?.code;
    const exchangeRate = this.shop()?.maintenanceShopCurrency?.exchangeRateFromUsd ?? 0;
    this.masterDataService
      .getRepairParts(modeCode, repairCode.code, shopCode, exchangeRate)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((parts) => {
        this.parts = parts;
        this.loadingParts.set(false);
      });
  }

  private addRequiredAndMaxValidators(selectedRepair: Repair) {
    let piecesControl = this.repairLineItemForm.controls.pieces;
    let materialCostPerPieceControl = this.repairLineItemForm.controls.materialCostPerPiece;
    let manHoursPerPieceControl = this.repairLineItemForm.controls.manHoursPerPiece;

    piecesControl.clearValidators();
    piecesControl.addValidators([Validators.required, Validators.pattern(/^(0|[1-9]\d*)$/), Validators.max(selectedRepair.maxQuantity)]);
    piecesControl.updateValueAndValidity();

    materialCostPerPieceControl.clearValidators();
    materialCostPerPieceControl.addValidators([
      Validators.required,
      Validators.pattern(/^\d*\.?\d{1,2}$/),
      Validators.max(utils.formatNumberToTwoDigitDecimal(selectedRepair.maxMaterialCost)),
    ]);
    materialCostPerPieceControl.updateValueAndValidity();

    manHoursPerPieceControl.clearValidators();
    manHoursPerPieceControl.addValidators([
      Validators.required,
      Validators.pattern(/^\d*\.?\d{1,2}$/),
      Validators.max(utils.formatNumberToTwoDigitDecimal(selectedRepair.maxLabourHours)),
    ]);
    manHoursPerPieceControl.updateValueAndValidity();
  }

  private mapCodeDescriptionToTypeaheadData(data: CodeDescription[]): IMcTypeaheadData[] {
    return data.map((codeDescription) => {
      return {
        label: codeDescription.code,
        sublabel: codeDescription.description,
        value: codeDescription.code,
      };
    });
  }

  private mapTPICodeToTypeaheadData(data: ThirdPartyIndicator[]): IMcTypeaheadData[] {
    return data.map((codeDescription) => {
      return {
        label: getTPIChar(codeDescription.code),
        sublabel: codeDescription.displayText,
        value: codeDescription.code,
      };
    });
  }

  private calculateRepairItemTotalCost() {
    combineLatest([
      this.repairLineItemForm.controls.pieces.valueChanges,
      this.repairLineItemForm.controls.materialCostPerPiece.valueChanges,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([pieces, materialCostPerPiece]) => {
        this.repairLineItemAmountWithoutParts = pieces * materialCostPerPiece;
        this.repairLineItemForm.controls.itemTotalCost.setValue(
          utils.numberFormatter.format(this.repairLineItemAmountWithoutParts + this.partsTotalAmount),
        );
      });
  }

  private addValidatorsToDamageCode() {
    let damageCode = this.repairLineItemForm.controls.damageCode;
    damageCode.clearValidators();
    damageCode.addValidators([Validators.required, typeaheadCodeExistValidator(this.damageCodesTypeahead)]);
    damageCode.updateValueAndValidity();
  }

  /**
   *
   * @param repairLineItem the line item to which this repair code belongs to
   * @description Add validators to the repair code control
   */
  private addValidatorsToRepairCode() {
    let repairCodeControl = this.repairLineItemForm.controls.repairCode;
    repairCodeControl.clearValidators();
    repairCodeControl.addValidators([Validators.required, typeaheadCodeExistValidator(this.repairCodesTypeahead)]);
  }

  /**
   *
   * @param repairLineItem the line item to which this repair location code belongs to
   * @description Add validators to the repair location code control
   */
  private addValidatorsToRepairLocationCode() {
    let repairLocationCode = this.repairLineItemForm.controls.repairLocationCode;
    repairLocationCode.clearValidators();
    repairLocationCode.addValidators([Validators.required, typeaheadCodeExistValidator(this.repairLocationCodesTypeahead)]);
    repairLocationCode.updateValueAndValidity();
  }

  /**
   *
   * @param repairLineItem the line item to which this tpi code belongs to
   * @description Add validators to the tpi code control
   */
  private addValidatorsToTpiCode() {
    let tpiCode = this.repairLineItemForm.controls.tpiCode;
    tpiCode.clearValidators();
    tpiCode.addValidators([Validators.required, typeaheadCodeExistValidator(this.tpiCodesTypeahead)]);
    tpiCode.updateValueAndValidity();
  }

  private isRepairCodePQCUpgrade(repairCode: string): boolean {
    const pqcUpgradeCodes = ['0910', '0911', '0922', '0923', '0924', '0925'];
    return pqcUpgradeCodes.includes(repairCode);
  }
}
